import {
  createChart,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type LogicalRange,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { useEffect, useMemo, useRef } from "react";
import type { HistoryPoint, OhlcCandle } from "../types";
import { normalizeSide } from "../lib/signals";
import { parseDbTimestamp } from "../lib/format";
import { bollingerBands, rsi } from "../lib/indicators";

interface Props {
  candles: OhlcCandle[] | null;
  history: HistoryPoint[] | null;
  /** Current live price — overlays a horizontal price line. */
  livePrice: number | null;
  /**
   * Changes to this key trigger a one-time fitContent after the candles load.
   * Use `${symbol}-${granularity}` so switching symbol or timeframe re-zooms.
   */
  fitKey?: string;
  /** BB params for the chart overlay. Defaults to standard (20, 2). */
  bbPeriod?: number;
  bbMult?: number;
  /** RSI period for the lower pane. Defaults to 14. */
  rsiPeriod?: number;
  /** RSI buy/sell thresholds shown as horizontal lines. */
  rsiBuyLevel?: number;
  rsiSellLevel?: number;
}

type PriceRefs = {
  chart: IChartApi;
  candle: ISeriesApi<"Candlestick">;
  upper: ISeriesApi<"Line">;
  middle: ISeriesApi<"Line">;
  lower: ISeriesApi<"Line">;
};

type RsiRefs = {
  chart: IChartApi;
  line: ISeriesApi<"Line">;
};

export function PriceChart({
  candles,
  history,
  livePrice,
  fitKey,
  bbPeriod = 20,
  bbMult = 2,
  rsiPeriod = 14,
  rsiBuyLevel = 40,
  rsiSellLevel = 60,
}: Props) {
  const priceRef = useRef<HTMLDivElement>(null);
  const rsiRef = useRef<HTMLDivElement>(null);
  const priceChart = useRef<PriceRefs | null>(null);
  const rsiChart = useRef<RsiRefs | null>(null);
  const livePriceLineRef = useRef<ReturnType<ISeriesApi<"Candlestick">["createPriceLine"]> | null>(null);
  const syncingRef = useRef(false);

  // Mount / unmount
  useEffect(() => {
    const priceEl = priceRef.current;
    const rsiEl = rsiRef.current;
    if (!priceEl || !rsiEl) return;

    const baseLayout = {
      background: { color: "transparent" as const },
      textColor: "#c9d1d9",
    };
    const baseGrid = {
      vertLines: { color: "rgba(255,255,255,0.04)" },
      horzLines: { color: "rgba(255,255,255,0.04)" },
    };
    const baseCrosshair = { mode: CrosshairMode.Normal };
    const rightScale = { borderColor: "rgba(255,255,255,0.1)" };

    const priceC = createChart(priceEl, {
      autoSize: true,
      layout: baseLayout,
      grid: baseGrid,
      crosshair: baseCrosshair,
      rightPriceScale: rightScale,
      timeScale: {
        borderColor: "rgba(255,255,255,0.1)",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time) => formatTickLocal(time),
      },
      localization: {
        timeFormatter: (time: Time) => formatCrosshairLocal(time),
        locale: navigator.language,
      },
    });

    const candle = priceC.addCandlestickSeries({
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderUpColor: "#22c55e",
      borderDownColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
    });

    const upper = priceC.addLineSeries({
      color: "rgba(239, 68, 68, 0.8)",
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const middle = priceC.addLineSeries({
      color: "rgba(156, 163, 175, 0.6)",
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      priceLineVisible: false,
      lastValueVisible: false,
    });
    const lower = priceC.addLineSeries({
      color: "rgba(34, 197, 94, 0.8)",
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    const rsiC = createChart(rsiEl, {
      autoSize: true,
      layout: baseLayout,
      grid: baseGrid,
      crosshair: baseCrosshair,
      rightPriceScale: {
        ...rightScale,
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      timeScale: {
        borderColor: "rgba(255,255,255,0.1)",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time) => formatTickLocal(time),
      },
      localization: {
        timeFormatter: (time: Time) => formatCrosshairLocal(time),
        locale: navigator.language,
      },
    });

    const rsiLine = rsiC.addLineSeries({
      color: "#a78bfa",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });
    // Lock RSI y-axis to the 0–100 band so the 40/60 threshold lines sit in
    // the same place regardless of the value range the current series happens
    // to traverse.
    rsiLine.applyOptions({
      priceFormat: { type: "price", precision: 1, minMove: 0.1 },
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: 0, maxValue: 100 },
      }),
    });
    rsiLine.createPriceLine({
      price: rsiBuyLevel,
      color: "rgba(34,197,94,0.6)",
      lineStyle: LineStyle.Dashed,
      lineWidth: 1,
      axisLabelVisible: true,
      title: String(rsiBuyLevel),
    });
    rsiLine.createPriceLine({
      price: rsiSellLevel,
      color: "rgba(239,68,68,0.6)",
      lineStyle: LineStyle.Dashed,
      lineWidth: 1,
      axisLabelVisible: true,
      title: String(rsiSellLevel),
    });

    priceChart.current = { chart: priceC, candle, upper, middle, lower };
    rsiChart.current = { chart: rsiC, line: rsiLine };

    // Bidirectional time-range sync with a guard to prevent feedback loops.
    const applyToRsi = (r: LogicalRange | null) => {
      if (syncingRef.current || !r) return;
      syncingRef.current = true;
      rsiC.timeScale().setVisibleLogicalRange(r);
      syncingRef.current = false;
    };
    const applyToPrice = (r: LogicalRange | null) => {
      if (syncingRef.current || !r) return;
      syncingRef.current = true;
      priceC.timeScale().setVisibleLogicalRange(r);
      syncingRef.current = false;
    };
    priceC.timeScale().subscribeVisibleLogicalRangeChange(applyToRsi);
    rsiC.timeScale().subscribeVisibleLogicalRangeChange(applyToPrice);

    return () => {
      priceC.timeScale().unsubscribeVisibleLogicalRangeChange(applyToRsi);
      rsiC.timeScale().unsubscribeVisibleLogicalRangeChange(applyToPrice);
      priceC.remove();
      rsiC.remove();
      priceChart.current = null;
      rsiChart.current = null;
      livePriceLineRef.current = null;
    };
  }, [rsiBuyLevel, rsiSellLevel]);

  const sortedCandles = useMemo(() => {
    if (!candles) return null;
    const seen = new Set<number>();
    return candles
      .map((c) => ({
        time: Math.floor(parseDbTimestamp(c.start) / 1000) as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }))
      .filter((p) => {
        if (seen.has(p.time as number)) return false;
        seen.add(p.time as number);
        return true;
      })
      .sort((a, b) => (a.time as number) - (b.time as number));
  }, [candles]);

  // Tracks how many bars were in the series before the current setData, so we
  // can tell if the user was looking at the right edge ("following live").
  const prevLenRef = useRef(0);
  const fittedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const r = priceChart.current;
    if (!r || !sortedCandles || sortedCandles.length === 0) return;

    const ts = r.chart.timeScale();
    const before = ts.getVisibleLogicalRange();
    const prevLen = prevLenRef.current;
    // "Following" if there's no prior range, or the right edge of the view is
    // within ~2 bars of the previous last bar. setData shifts indices when the
    // oldest bar is trimmed off the front, so we compare against prevLen.
    const wasFollowing =
      !before || prevLen === 0 || before.to >= prevLen - 2;

    r.candle.setData(sortedCandles);
    prevLenRef.current = sortedCandles.length;

    const isFirstFit = fittedKeyRef.current !== fitKey;
    if (isFirstFit || wasFollowing) {
      const len = sortedCandles.length;
      const target = targetBarsForWidth(priceRef.current?.clientWidth);
      ts.setVisibleLogicalRange({
        from: Math.max(0, len - target),
        to: len + 2,
      });
      fittedKeyRef.current = fitKey ?? null;
    }
  }, [sortedCandles, fitKey]);

  // BB from closes
  const bbSeries = useMemo(() => {
    if (!sortedCandles) return null;
    const closes = sortedCandles.map((c) => c.close);
    const bands = bollingerBands(closes, bbPeriod, bbMult);
    const upper: { time: UTCTimestamp; value: number }[] = [];
    const middle: { time: UTCTimestamp; value: number }[] = [];
    const lower: { time: UTCTimestamp; value: number }[] = [];
    for (let i = 0; i < sortedCandles.length; i++) {
      const b = bands[i];
      if (!b) continue;
      const t = sortedCandles[i].time;
      upper.push({ time: t, value: b.upper });
      middle.push({ time: t, value: b.middle });
      lower.push({ time: t, value: b.lower });
    }
    return { upper, middle, lower };
  }, [sortedCandles, bbPeriod, bbMult]);

  useEffect(() => {
    const r = priceChart.current;
    if (!r || !bbSeries) return;
    r.upper.setData(bbSeries.upper);
    r.middle.setData(bbSeries.middle);
    r.lower.setData(bbSeries.lower);
  }, [bbSeries]);

  // RSI from closes (client-side)
  const rsiSeries = useMemo(() => {
    if (!sortedCandles) return null;
    const closes = sortedCandles.map((c) => c.close);
    const values = rsi(closes, rsiPeriod);
    const out: { time: UTCTimestamp; value: number }[] = [];
    for (let i = 0; i < sortedCandles.length; i++) {
      const v = values[i];
      if (v == null || !Number.isFinite(v)) continue;
      out.push({ time: sortedCandles[i].time, value: v });
    }
    return out;
  }, [sortedCandles, rsiPeriod]);

  useEffect(() => {
    const r = rsiChart.current;
    if (!r || !rsiSeries) return;
    r.line.setData(rsiSeries);
  }, [rsiSeries]);

  // Signal markers — only emit on transitions, then dedupe to one marker per
  // (chart bar, type) so higher timeframes don't stack many arrows on a single
  // bar when the underlying 5s history flips several times within it.
  useEffect(() => {
    const r = priceChart.current;
    if (!r || !history) return;
    const sorted = [...history].sort(
      (a, b) => parseDbTimestamp(a.bucket) - parseDbTimestamp(b.bucket)
    );
    const raw: SeriesMarker<Time>[] = [];
    let prevInit: "BUY" | "SELL" | null = null;
    let prevRec: "BUY" | "SELL" | null = null;
    for (const p of sorted) {
      const t = Math.floor(parseDbTimestamp(p.bucket) / 1000) as UTCTimestamp;
      const init = normalizeSide(p.initial_trade);
      const rec = normalizeSide(p.recovery_trade);
      if (rec && rec !== prevRec) {
        raw.push({
          time: t,
          position: rec === "BUY" ? "belowBar" : "aboveBar",
          color: rec === "BUY" ? "#22c55e" : "#ef4444",
          shape: rec === "BUY" ? "arrowUp" : "arrowDown",
          text: `R·${rec}`,
        });
      } else if (!rec && init && init !== prevInit) {
        raw.push({
          time: t,
          position: init === "BUY" ? "belowBar" : "aboveBar",
          color: init === "BUY" ? "#16a34a" : "#dc2626",
          shape: init === "BUY" ? "arrowUp" : "arrowDown",
          text: `I·${init}`,
        });
      }
      prevInit = init;
      prevRec = rec;
    }

    let markers = raw;
    if (sortedCandles && sortedCandles.length > 1) {
      const barSec =
        (sortedCandles[1].time as number) - (sortedCandles[0].time as number);
      if (barSec > 0) {
        // Collapse all markers within a bar to a single marker reflecting the
        // bar's final state. Iteration order on raw is chronological, so the
        // last `set` per bucket wins.
        const bucketed = new Map<number, SeriesMarker<Time>>();
        for (const m of raw) {
          const bucket = Math.floor((m.time as number) / barSec) * barSec;
          bucketed.set(bucket, { ...m, time: bucket as UTCTimestamp });
        }
        markers = Array.from(bucketed.values()).sort(
          (a, b) => (a.time as number) - (b.time as number)
        );
      }
    }
    r.candle.setMarkers(markers);
  }, [history, sortedCandles]);

  // Live price line
  useEffect(() => {
    const r = priceChart.current;
    if (!r) return;
    if (livePrice == null) return;
    if (livePriceLineRef.current) {
      livePriceLineRef.current.applyOptions({ price: livePrice });
    } else {
      livePriceLineRef.current = r.candle.createPriceLine({
        price: livePrice,
        color: "#60a5fa",
        lineStyle: LineStyle.Dashed,
        lineWidth: 1,
        axisLabelVisible: true,
        title: "live",
      });
    }
  }, [livePrice]);

  return (
    <div className="chart-stack">
      <div ref={priceRef} className="price-chart" />
      <div ref={rsiRef} className="rsi-chart" />
    </div>
  );
}

// Picks how many bars to show in the live-follow view. Aims for ~6–8 px per
// bar, clamped so narrow phones still show a useful slice and very wide
// monitors don't pack the whole 300-bar buffer into the viewport.
function targetBarsForWidth(width: number | undefined): number {
  const w = width && width > 0 ? width : 800;
  const bars = Math.floor(w / 7);
  return Math.max(50, Math.min(180, bars));
}

function toDate(time: Time): Date | null {
  if (typeof time === "number") return new Date(time * 1000);
  if (typeof time === "string") return new Date(time);
  return null;
}

function formatTickLocal(time: Time): string {
  const d = toDate(time);
  if (!d) return "";
  if (d.getHours() === 0 && d.getMinutes() === 0) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatCrosshairLocal(time: Time): string {
  const d = toDate(time);
  if (!d) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
